import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type { DetailedChangeset } from 'ember-changeset/types';
import { restartableTask, timeout } from 'ember-concurrency';
import type GlobalIdScanPageModel from 'garaje/models/global-id-scan-page';
import updateAndSaveTask from 'garaje/utils/decorators/update-and-save-task';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import zft from 'garaje/utils/zero-for-tests';

// list of roles that can be searched for to add as outgoing-invite-approval reviewers
const SEARCH_ROLE_LIST = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

@updateAndSaveTask
class LocationOverviewSignInFlowsShowSecurityController extends Controller {
  @service declare store: Store;

  idScanChangeset!: DetailedChangeset<GlobalIdScanPageModel>;

  @action
  handleIdScanPageChanges(changeset: DetailedChangeset<GlobalIdScanPageModel>): void {
    this.idScanChangeset = changeset;
  }

  /**
   * Task for searching for users that can be added as invite approval reviewers. This overrides
   * the default search in the Flows::Sections::Security::InviteApproval component which includes
   * a filter for the current location (which isn't applicable here since we're on the Global Overview).
   * This version searches for users with the same roles (Global Admin, Location Admin, or Front Desk Admin/Receptionist)
   * without a location filter.
   *
   * NOTE: If you change the role checking logic here (SEARCH_ROLE_LIST), you must also update:
   * - app/pods/components/flows/sections/security/approval-chains/component.ts (searchUsers)
   * - app/pods/components/flows/sections/security/invite-approval/component.ts (searchUsersTask)
   */
  searchUsers = restartableTask(async (term: string) => {
    await timeout(zft(250));

    const roles = await this.store.query('user-role', {
      filter: { query: term, roles: SEARCH_ROLE_LIST.join(','), confirmedUsers: true },
    });
    const users = await Promise.all(roles.map((role) => role.user));

    return users;
  });
}

export default LocationOverviewSignInFlowsShowSecurityController;
